import React, { useRef, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { EmailPreviewLogic } from '../logic'
import { Card } from '~common/components/card'
import { LoadingIndicator } from '~common/components/loading'
import { InvoiceMetadataDisplay } from '~/features/ui-components/invoice-metadata-display'
import type { EmailPreviewProps } from '../types'
import { PdfViewer } from '~/features/ui-components/pdf-viewer'
import { Button } from '~common/components/button'
import { VendorDisplay } from '~/features/ui-components/vendor-display'
import { Icon } from '~common/components/icon'
import { AttachmentDropdown } from '~/features/ui-components/attachment-dropdown'
import { useCacheValue } from '~/cache-context'
import type {
    DocumentTransactionAssociation,
    BankTransaction,
} from '~/storage/types'
import { formatISODate } from '~common/utils/date-transformation/date-transformations'
import { TaskState } from '~/services/cache/types'

/**
 * Adds target="_blank" and rel="noopener noreferrer" to all &lt;a&gt; tags without a target attribute.
 * This ensures links open in new tabs securely.
 */
function addTargetBlankToLinks(html: string): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const links = doc.querySelectorAll('a[href]')
    links.forEach((link) => {
        const anchor = link as HTMLAnchorElement
        if (!anchor.hasAttribute('target')) {
            anchor.setAttribute('target', '_blank')
        }
        // Add rel for security
        const rel = anchor.getAttribute('rel') || ''
        if (!rel.includes('noopener')) {
            anchor.setAttribute(
                'rel',
                rel ? `${rel} noopener noreferrer` : 'noopener noreferrer',
            )
        }
    })
    return doc.body.innerHTML
}

export const EmailAndInvoicePreview: React.FC<EmailPreviewProps> = (props) => {
    const theme = useTheme()
    const { logic, state } = useLogic(EmailPreviewLogic, props)
    const iframeRef = useRef<HTMLIFrameElement>(null)

    useEffect(() => {
        const iframe = iframeRef.current
        if (!iframe) return

        let clickHandler: ((event: MouseEvent) => void) | null = null

        const handleIframeLoad = () => {
            const iframeDoc = iframe.contentDocument
            if (!iframeDoc) return

            if (clickHandler) {
                iframeDoc.removeEventListener('click', clickHandler)
            }

            clickHandler = (event: MouseEvent) => {
                const target = event.target as HTMLElement
                const link = target.closest('a')
                if (link && link.href) {
                    event.preventDefault()
                    event.stopPropagation()
                    window.open(link.href, '_blank', 'noopener,noreferrer')
                }
            }

            iframeDoc.addEventListener('click', clickHandler)
        }

        iframe.addEventListener('load', handleIframeLoad)
        if (iframe.contentDocument?.readyState === 'complete') {
            handleIframeLoad()
        }

        return () => {
            iframe.removeEventListener('load', handleIframeLoad)
            if (clickHandler && iframe.contentDocument) {
                iframe.contentDocument.removeEventListener(
                    'click',
                    clickHandler,
                )
            }
        }
    }, [state.emailLoadState, state.emailData?.htmlContent])

    const matchesByInvoice = useCacheValue('matchesByInvoice')
    const documentTransactionAssociations = useCacheValue(
        'documentTransactionAssociations',
    )
    const transactions = useCacheValue('transactions')
    const cacheLoadState = useCacheValue('loadState')
    const settings = useCacheValue('settings')

    let matchingTransactions: Array<{
        association: DocumentTransactionAssociation
        transaction: BankTransaction
    }> = []

    if (
        state.invoiceDocumentId &&
        cacheLoadState === 'success' &&
        matchesByInvoice &&
        documentTransactionAssociations &&
        transactions
    ) {
        const assocIds = matchesByInvoice[state.invoiceDocumentId] ?? []
        const matches: Array<{
            association: DocumentTransactionAssociation
            transaction: BankTransaction
        }> = []

        for (const assocId of assocIds) {
            const assoc = documentTransactionAssociations[assocId]
            if (assoc) {
                const transaction = transactions[assoc.transactionId]
                if (transaction) {
                    matches.push({ association: assoc, transaction })
                }
            }
        }

        matchingTransactions = matches.sort((a, b) => {
            if (a.association.method === 'user_confirmed') return -1
            if (b.association.method === 'user_confirmed') return 1
            return b.association.confidence - a.association.confidence
        })
    }

    const confirmedMatches = matchingTransactions.filter(
        (match) => match.association.method === 'user_confirmed',
    )
    const transactionsToDisplay =
        confirmedMatches.length > 0 ? confirmedMatches : matchingTransactions
    const hasConfirmedMatches = confirmedMatches.length > 0

    if (state.loadState === 'running') {
        return (
            <Card padding="large">
                <LoadingContainer>
                    <LoadingIndicator />
                    <span>Loading.</span>
                </LoadingContainer>
            </Card>
        )
    }

    if (state.loadState === 'error') {
        return (
            <Card padding="large">
                <ErrorContainer>
                    <ErrorText>
                        {state.error || 'Failed to load email'}
                    </ErrorText>
                </ErrorContainer>
            </Card>
        )
    }

    const email = state.emailData
    const hasInvoice = state.invoiceDocumentId !== null

    const handleEmailDownload = async () => {
        if (!iframeRef.current || !state.emailData) {
            console.error('Iframe not available or no email data')
            return
        }

        const iframeDoc = iframeRef.current.contentDocument
        if (!iframeDoc || !iframeDoc.body) {
            console.error('Cannot access iframe document')
            return
        }

        const fullHtml = iframeDoc.documentElement.outerHTML
        await logic.downloadEmailAsPdf(fullHtml)
    }

    return (
        <Card padding="none">
            <Container>
                {state.invoiceDocumentId &&
                    (transactionsToDisplay.length > 0 ||
                        state.matchLoadingState === 'running') && (
                        <Section>
                            {/* Match Status Banner */}
                            <MatchStatusBanner>
                                <MatchStatusHeader>
                                    <Icon
                                        name={
                                            hasConfirmedMatches
                                                ? 'check-circle'
                                                : 'magic'
                                        }
                                        iconColor={'info'}
                                    />
                                    <MatchStatusTitle>
                                        {hasConfirmedMatches
                                            ? `Matched to ${transactionsToDisplay.length} ${
                                                  transactionsToDisplay.length ===
                                                  1
                                                      ? 'transaction'
                                                      : 'transactions'
                                              }`
                                            : `Suggested Transactions`}
                                    </MatchStatusTitle>
                                </MatchStatusHeader>
                                {state.matchLoadingState === 'running' &&
                                transactionsToDisplay.length === 0 ? (
                                    <MatchLoadingContainer>
                                        <LoadingIndicator />
                                        <span>Updating matches...</span>
                                    </MatchLoadingContainer>
                                ) : (
                                    <MatchList>
                                        {transactionsToDisplay.map((match) => {
                                            const date = formatISODate(
                                                new Date(
                                                    match.transaction.date,
                                                ).toISOString(),
                                                settings?.dateFormat ??
                                                    'MM/DD/YYYY',
                                            )
                                            const currency =
                                                match.transaction.currency || ''
                                            const amount =
                                                match.transaction.amount
                                            const vendorName =
                                                match.transaction.vendorName ||
                                                'Unknown Vendor'
                                            const confidence = Math.round(
                                                match.association.confidence,
                                            )
                                            const isConfirmed =
                                                match.association.method ===
                                                'user_confirmed'

                                            return (
                                                <MatchItem
                                                    key={match.association.id}
                                                >
                                                    <MatchItemContent>
                                                        <MatchItemMain>
                                                            <MatchItemVendor>
                                                                {vendorName}
                                                                {match
                                                                    .transaction
                                                                    .referenceText
                                                                    ? ': ' +
                                                                      match
                                                                          .transaction
                                                                          .referenceText
                                                                    : ''}
                                                            </MatchItemVendor>
                                                            <MatchItemDetails>
                                                                {date} •{' '}
                                                                {currency}{' '}
                                                                {amount.toFixed(
                                                                    2,
                                                                )}
                                                            </MatchItemDetails>
                                                        </MatchItemMain>
                                                        <MatchPercentage>
                                                            {`${confidence}%`}
                                                        </MatchPercentage>
                                                        {!isConfirmed ? (
                                                            <Button
                                                                label={
                                                                    'Confirm'
                                                                }
                                                                variant="secondary"
                                                                size="small"
                                                                icon="check"
                                                                onClick={() =>
                                                                    logic.confirmMatch(
                                                                        match
                                                                            .association
                                                                            .id,
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            <Button
                                                                label={'Remove'}
                                                                variant="secondary"
                                                                size="small"
                                                                icon="close"
                                                                onClick={() =>
                                                                    logic.removeMatch(
                                                                        match
                                                                            .association
                                                                            .id,
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </MatchItemContent>
                                                </MatchItem>
                                            )
                                        })}
                                    </MatchList>
                                )}
                            </MatchStatusBanner>
                        </Section>
                    )}
                <Section>
                    <MetadataSection>
                        {email && (
                            <EmailHeader>
                                <TopAreaEmailHeader>
                                    <SubjectText>
                                        <Icon
                                            name={'mail'}
                                            iconColor={'info'}
                                        />
                                        {email.subject || '(no subject)'}
                                    </SubjectText>
                                    <DateText>
                                        {new Date(
                                            email.receivedDate,
                                        ).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </DateText>
                                </TopAreaEmailHeader>
                                <BottomAreaEmailHeader>
                                    <SenderInfo>
                                        <VendorDisplay
                                            vendorName={email.fromName}
                                            vendorEmail={email.fromEmail}
                                        />
                                    </SenderInfo>
                                    <AttachmentsInfo>
                                        {email.originalAttachments.length >
                                            0 && (
                                            <>
                                                {email.originalAttachments
                                                    .length === 1 ? (
                                                    // Single attachment - show individual button
                                                    <AttachmentsList>
                                                        {email.originalAttachments.map(
                                                            (attachment) => {
                                                                const isDownloading =
                                                                    state.downloadingAttachmentId ===
                                                                    attachment.id
                                                                return (
                                                                    <Button
                                                                        key={
                                                                            attachment.id
                                                                        }
                                                                        onClick={() =>
                                                                            !isDownloading &&
                                                                            logic.downloadAttachment(
                                                                                attachment.id,
                                                                            )
                                                                        }
                                                                        label={
                                                                            <LabelContent>
                                                                                {isDownloading
                                                                                    ? 'Downloading...'
                                                                                    : attachment.filename}
                                                                            </LabelContent>
                                                                        }
                                                                        icon={
                                                                            'download'
                                                                        }
                                                                        variant="secondary"
                                                                        size="small"
                                                                        fullWidth
                                                                    />
                                                                )
                                                            },
                                                        )}
                                                    </AttachmentsList>
                                                ) : (
                                                    // Multiple attachments - show dropdown
                                                    <AttachmentDropdown
                                                        attachments={email.originalAttachments.map(
                                                            (attachment) => ({
                                                                id: attachment.id,
                                                                filename:
                                                                    attachment.filename,
                                                                size: attachment.size,
                                                            }),
                                                        )}
                                                        onDownload={
                                                            logic.downloadAttachment
                                                        }
                                                        downloadingAttachmentId={
                                                            state.downloadingAttachmentId
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                    </AttachmentsInfo>
                                </BottomAreaEmailHeader>
                            </EmailHeader>
                        )}
                    </MetadataSection>
                </Section>

                {/* Invoice Metadata Section - Only show if invoice document ID exists */}
                {state.invoiceDocumentId && (
                    <Section>
                        <InvoiceMetadataDisplay
                            invoiceId={state.invoiceDocumentId}
                            services={props.services}
                            storageModules={props.storageModules}
                            onUpdateMatches={async (state: TaskState) => {
                                logic.updateMatchLoadingState(state)
                            }}
                        />
                    </Section>
                )}

                {email && (
                    <ToggleSection>
                        <ToggleButton
                            onClick={() => logic.setDocumentView('email')}
                            disabled={state.emailLoadState === 'running'}
                            label={'Email'}
                            fullWidth
                            variant={
                                state.documentToShow === 'email'
                                    ? 'primary'
                                    : 'secondary'
                            }
                            size="medium"
                            icon={
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleEmailDownload()
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Icon name="download" />
                                </span>
                            }
                        />
                        <ToggleButton
                            onClick={() => {
                                if (hasInvoice) {
                                    logic.setDocumentView('invoice')
                                } else {
                                    logic.analyzeMessage(email)
                                    logic.setDocumentView('invoice')
                                }
                            }}
                            disabled={state.pdfLoadState === 'running'}
                            label={
                                hasInvoice ? 'Invoice' : 'Analyze for Invoice'
                            }
                            fullWidth
                            variant={
                                state.documentToShow === 'invoice'
                                    ? 'primary'
                                    : 'secondary'
                            }
                            size="medium"
                        />
                    </ToggleSection>
                )}

                <EmailViewerSection>
                    {state.documentToShow === 'email' && email && (
                        <>
                            {state.emailLoadState === 'running' && (
                                <LoadingContainer>
                                    <LoadingIndicator />
                                    <span>Loading email content...</span>
                                </LoadingContainer>
                            )}
                            {state.emailLoadState === 'error' && (
                                <ErrorContainer>
                                    <ErrorText>
                                        Failed to load email content
                                    </ErrorText>
                                </ErrorContainer>
                            )}
                            {state.emailLoadState === 'success' &&
                                email.htmlContent && (
                                    <EmailIframe
                                        ref={iframeRef}
                                        srcDoc={addTargetBlankToLinks(
                                            email.htmlContent,
                                        )}
                                        sandbox="allow-same-origin allow-popups"
                                        title="Email Content"
                                        style={{
                                            border: 'none',
                                        }}
                                    />
                                )}
                            {state.emailLoadState === 'success' &&
                                !email.htmlContent && (
                                    <ErrorContainer>
                                        <ErrorText>
                                            Email content not available
                                        </ErrorText>
                                    </ErrorContainer>
                                )}
                        </>
                    )}
                    {((state.documentToShow === 'invoice' &&
                        state.invoiceDocumentId) ||
                        state.pdfLoadState === 'running') && (
                        <>
                            {state.pdfLoadState === 'running' && (
                                <LoadingContainer>
                                    <LoadingIndicator />
                                    <span>Loading PDF...</span>
                                </LoadingContainer>
                            )}
                            {state.pdfLoadState === 'error' && (
                                <ErrorContainer>
                                    <ErrorText>Failed to load PDF</ErrorText>
                                </ErrorContainer>
                            )}
                            {(state.pdfLoadState === 'success' ||
                                state.pdfLoadState === 'pristine') && (
                                <PDFViewerContainer>
                                    <PdfViewer pdfBlob={state.pdfBlob} />
                                </PDFViewerContainer>
                            )}
                        </>
                    )}
                </EmailViewerSection>
            </Container>
        </Card>
    )
}

// Styled Components
const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    gap: ${({ theme }) => theme.spacing[1]};
`

const MetadataSection = styled.div`
    border-radius: ${({ theme }) => theme.borderRadius['2xl']};
    box-sizing: border-box;
    background: ${({ theme }) => theme.colors.background.bg2};
`

const EmailHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => theme.spacing[4]};
`

const SubjectText = styled.h1`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
    max-width: 70%;
`

const SenderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    max-width: 60%;
`

const DateText = styled.span`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: 400;
    line-height: 1.7797887802124024em;
    color: ${({ theme }) => theme.colors.text.tertiary};
`

const EmailViewerSection = styled.div`
    flex: 1;
    border: 1px solid ${({ theme }) => theme.colors.greyScale[200]};
    border-radius: 16px;
    margin: ${({ theme }) => theme.spacing[3]};
    margin-top: 0;
    box-sizing: border-box;
    margin-bottom: ${({ theme }) => theme.spacing[40]};
    min-height: 1000px;
    height: fit-content;
    overflow: hidden;
`

const EmailIframe = styled.iframe`
    width: 100%;
    min-height: 800px;
    height: 100%;
    border-radius: 16px;
    overflow: hidden;
    border: none;
    flex: 1;
    background: white;
`

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    gap: ${({ theme }) => theme.spacing[3]};
    color: ${({ theme }) => theme.colors.text.primary};
`

const ErrorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    padding: ${({ theme }) => theme.spacing[4]};
`

const ErrorText = styled.p`
    color: ${({ theme }) => theme.colors.error[600]};
    margin: 0;
    text-align: center;
`

const AttachmentsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
    width: fit-content;
    max-width: 200px;
    width: fit-content;
`

const AttachmentItem = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
    color: ${({ theme }) => theme.colors.text.tertiary};
`

const PDFViewerContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    gap: ${({ theme }) => theme.spacing[3]};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    overflow: hidden;
    height: 100%;
    min-height: 1000px;
`

const ToggleSection = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => theme.spacing[3]};
    background: ${({ theme }) => theme.colors.background.bg1};
`

const ToggleButton = styled(Button)``

const SplitViewContainer = styled.div`
    display: flex;
    height: 100%;
    gap: ${({ theme }) => theme.spacing[1]};
`

const SplitViewPane = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0; /* Allow flex items to shrink below their minimum content size */
`

const SplitViewDivider = styled.div`
    width: 1px;
    background: ${({ theme }) => theme.colors.border.primary};
    flex-shrink: 0;
`

const TopAreaEmailHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`

const BottomAreaEmailHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`

const AttachmentsInfo = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`

const LabelContent = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const MatchStatusBanner = styled.div`
    border-radius: ${({ theme }) => theme.borderRadius['2xl']};
    background: ${({ theme }) => theme.colors.background.bg2};
    padding: ${({ theme }) => theme.spacing[3]};
`

const MatchStatusHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    margin-bottom: ${({ theme }) => theme.spacing[3]};
`

const MatchStatusTitle = styled.h3`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
`

const MatchList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`

const MatchItem = styled.div`
    padding: ${({ theme }) => theme.spacing[3]};
    border-radius: ${({ theme }) => theme.borderRadius['xl']};
    background: ${({ theme }) => theme.colors.background.bg3};
    border: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
`

const MatchItemContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing[3]};
`

const MatchItemMain = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
    flex: 1;
    min-width: 0;
`

const MatchItemVendor = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const MatchItemDetails = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.text.secondary};
`

const MatchPercentage = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`

const Section = styled.div<{ marginTop?: string }>`
    padding: ${({ theme }) => theme.spacing[3]};
    gap: ${({ theme }) => theme.spacing[2]};
    margin-top: ${({ marginTop }) => marginTop ?? '0'};
    display: flex;
    flex-direction: column;
`

const MatchLoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing[4]};
    gap: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.text.primary};
`
